import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

interface DraggableProps {
    horizontalPosition: number;
    draggableRadius: number;
    styledButtonRadius: number;
    onChange: (value: number) => void;
    minSliderPosX: number;
    maxSliderPosX: number;
    imperativePosition: number | undefined;
    maxRange: number;
    isSingleValue: boolean;
}

const Container = styled.div.attrs<{
    x: number;
    draggableRadius: number;
    styledButtonRadius: number;
}>(({ x, draggableRadius, styledButtonRadius }) => ({
    style: {
        transform: `translateX(${
            x - (draggableRadius - styledButtonRadius / 2)
        }px)`,
    },
}))<{
    x: number;
    draggableRadius: number;
    styledButtonRadius: number;
    isDragging: boolean;
}>`
    position: absolute;
    ${({ draggableRadius }): string =>
        `
        width: ${draggableRadius * 2}px;
        height: ${draggableRadius * 2}px;
        border-radius: ${draggableRadius * 2}px;
        margin-top: -${draggableRadius + 2}px;
    `}
`;

const StyledButton = styled.div<{
    draggableRadius: number;
    styledButtonRadius: number;
}>`
    background-color: ${({ theme: { palette } }): string =>
        palette.primary[500]};
    position: absolute;
    cursor: pointer;
    ${({ draggableRadius, styledButtonRadius }): string =>
        `
        width: ${styledButtonRadius * 2}px;
        height: ${styledButtonRadius * 2}px;
        border-radius: ${styledButtonRadius * 2}px;
        top: ${draggableRadius - styledButtonRadius}px;
        left: ${draggableRadius - styledButtonRadius}px;
    `}
`;

const Draggable: React.FunctionComponent<DraggableProps> = ({
    horizontalPosition,
    draggableRadius,
    styledButtonRadius,
    onChange,
    minSliderPosX,
    maxSliderPosX,
    imperativePosition,
    maxRange,
    isSingleValue,
    ...props
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [translateX, setTranslateX] = useState(
        horizontalPosition ? horizontalPosition : minSliderPosX,
    );
    const [initialOffsetX, setInitialOffsetX] = useState(0);
    const [initialClientX, setInitialClientX] = useState(0);

    // Updates `posX` whenever the `posX` value provide by the input changes
    useEffect(() => {
        if (
            typeof imperativePosition !== 'undefined' &&
            imperativePosition >= 0
        ) {
            setTranslateX(imperativePosition);
        }
    }, [imperativePosition]);

    const handleNewSliderPosition = useCallback(
        (value: number): void => {
            if (isDragging) {
                const currentUpdatedPosX =
                    initialOffsetX + value - initialClientX;
                // Get the new posX from:
                // (initialElementX + newMouseX) - initialMouseX
                const maxSliderX = isSingleValue ? maxRange : maxSliderPosX;

                if (
                    currentUpdatedPosX > minSliderPosX &&
                    currentUpdatedPosX < maxSliderX
                ) {
                    onChange(currentUpdatedPosX);
                    setTranslateX(currentUpdatedPosX);
                }
                if (currentUpdatedPosX >= maxSliderX) {
                    onChange(maxSliderX);
                    setTranslateX(maxSliderX);
                }
            }
        },
        [
            initialClientX,
            initialOffsetX,
            isDragging,
            isSingleValue,
            maxRange,
            maxSliderPosX,
            minSliderPosX,
            onChange,
        ],
    );

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            const value = event.clientX;

            handleNewSliderPosition(value);
        },
        [handleNewSliderPosition],
    );

    const handleTouchMove = useCallback(
        (event: TouchEvent) => {
            const value = event.touches[0].clientX;

            handleNewSliderPosition(value);
        },
        [handleNewSliderPosition],
    );

    // Handler executed when releasing the mouse click from the element
    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
        }
    }, [isDragging]);

    // Handler executed when clicking the element
    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        setIsDragging(true);
        setInitialClientX(event.clientX);
        setInitialOffsetX(translateX);
    };

    // handleTouchStart
    const handleTouchStart = (
        event: React.TouchEvent<HTMLDivElement>,
    ): void => {
        setIsDragging(true);
        const touch = event.touches[0];

        setInitialClientX(touch.clientX);
        setInitialOffsetX(translateX);
    };

    const handleTouchEnd = useCallback((): void => {
        if (isDragging) {
            setIsDragging(false);
        }
    }, [isDragging]);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleTouchMove);
        window.addEventListener('touchend', handleTouchEnd);

        return (): void => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleMouseMove, handleMouseUp, handleTouchEnd, handleTouchMove]);

    return (
        <Container
            isDragging={isDragging}
            draggableRadius={draggableRadius}
            styledButtonRadius={styledButtonRadius}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            x={translateX}
            {...props}
        >
            <StyledButton
                draggableRadius={draggableRadius}
                styledButtonRadius={styledButtonRadius}
            />
        </Container>
    );
};

export default Draggable;
